import { useState, useEffect, useRef } from 'react';
import { B, radius, weight } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Spinner } from '../ui';
import {
  SENTIMENT_ORDER, splitBriefSections, parseStatements, matchSentiment,
  groupKeyFindings, tallyFindings, cashflowSummary, sourceLabel, sectionPreview,
} from '../../utils/dealResearch';

// Deal Research tab — the report a research run produces for a deal.
//
// The page is a DILIGENCE MEMO, not a settings screen, and it is laid out as
// one: a masthead carrying the run's provenance, a scorecard of the figures a
// reader decides on, then the analyst's bottom line, the cash flow, the graded
// key findings, and the raw agent output behind category tabs. A contents rail
// tracks position through what is often several screens of report.
//
// Everything below is presentation over what `GET /deals/{id}/research` already
// returns — no new endpoint, no new brand tokens. Backend: api/src/functions/dealResearch.js.
//
// The two datasets the API returns are DELIBERATELY KEPT APART, because they are
// not the same thing and neither can stand in for the other:
//   • `keyFindings[]` — group · topic · sentiment · lead · detail. Graded, but
//     carries no source and no confidence. This is the evidence.
//   • `findings[]`    — category · title · summary · sourceUrl · confidence.
//     Sourced and scored, but ungraded. This is what the agents actually read.
// Fusing them would mean inventing a sentiment for one or a source for the other.

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

const SENTIMENT = {
  positive: { label: 'Positive', color: B.green, legend: 'Positive / verified' },
  flag:     { label: 'Flag',     color: B.amber, legend: 'Flag — needs diligence' },
  risk:     { label: 'Risk',     color: B.red,   legend: 'Material risk' },
};

const CATEGORY_META = {
  web:            { label: 'Company & sponsor',        short: 'Company',        color: B.navy },
  public_records: { label: 'Public & gov. records',    short: 'Public records', color: B.tealDk },
  parcel:         { label: 'Parcel & land',            short: 'Parcel',         color: B.green },
  market:         { label: 'Market & comparables',     short: 'Market',         color: B.amber },
};
const CATEGORY_ORDER = ['web', 'public_records', 'parcel', 'market'];

const POLL_MS = 5000;
// Poll long enough to cover the longest run (a 9-min "without limit" run) plus
// the server's 12-min stale-pending self-heal, so a stuck run resolves on its
// own here (~13 min) instead of the tab spinning until a manual refresh.
const POLL_CAP = 160;

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------

const Chevron = ({ open, size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false"
    style={{ flex: 'none', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.16s ease' }}>
    <path d="M6 3.5 L10.5 8 L6 12.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Panel = ({ children, style }) => (
  <div style={{
    background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xxl,
    boxShadow: B.shadowXs, overflow: 'hidden', ...style,
  }}>{children}</div>
);

const CardHead = ({ title, hint, action }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
    borderBottom: `1px solid ${B.divider}`,
  }}>
    <h4 style={{ margin: 0, fontSize: 13.5, fontWeight: weight.semibold, color: B.text, letterSpacing: -0.05 }}>{title}</h4>
    {hint && <span style={{ fontSize: 11.5, color: B.textLt, marginLeft: 'auto' }}>{hint}</span>}
    {action && <span style={{ marginLeft: hint ? 0 : 'auto' }}>{action}</span>}
  </div>
);

const Pill = ({ children, color = B.textMd, solid = false }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: radius.pill,
    padding: '3px 10px', fontSize: 11.5, fontWeight: weight.semibold, whiteSpace: 'nowrap',
    background: solid ? color : `${color}14`,
    border: `1px solid ${solid ? color : `${color}3d`}`,
    color: solid ? B.white : color,
  }}>{children}</span>
);

const Dot = ({ color, size = 7 }) => (
  <span aria-hidden="true" style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'inline-block', flex: 'none' }} />
);

const Sep = () => <span aria-hidden="true" style={{ color: B.border }}>·</span>;

// A source rendered as its host, not as a raw URL that wraps mid-word.
const SourceChip = ({ url }) => {
  const label = sourceLabel(url);
  if (!label) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={url}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: B.textMd,
        border: `1px solid ${B.border}`, borderRadius: radius.md, padding: '2px 7px',
        background: B.surfaceMuted, textDecoration: 'none', maxWidth: 240,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
      <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 3, background: B.accent, opacity: 0.5, flex: 'none' }} />
      {label}
    </a>
  );
};

// Confidence as a meter — a number the eye can compare, not the string "72% conf.".
const ConfidenceMeter = ({ value }) => {
  if (value == null) return null;
  const pct = Math.max(0, Math.min(1, Number(value) || 0));
  return (
    <span title={`${fmt.pct(pct)} confidence`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: B.textLt }}>
      <span aria-hidden="true" style={{ width: 34, height: 4, borderRadius: 2, background: B.surfaceSunken, overflow: 'hidden', display: 'block' }}>
        <span style={{ display: 'block', height: '100%', width: `${pct * 100}%`, background: B.textLt }} />
      </span>
      {Math.round(pct * 100)}%
    </span>
  );
};

// A collapsible section. Closed, it still says what it is holding.
const Collapsible = ({ title, preview, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${B.divider}` }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          background: 'none', border: 'none', padding: '12px 18px', cursor: 'pointer',
          fontFamily: 'inherit', color: B.textLt,
        }}>
        <Chevron open={open} />
        <span style={{ fontWeight: weight.semibold, fontSize: 13.5, color: B.text }}>{title}</span>
        {preview && <span style={{ color: B.textLt, fontSize: 11.5, marginLeft: 'auto' }}>{preview}</span>}
      </button>
      {open && <div style={{ padding: '0 18px 16px 40px' }}>{children}</div>}
    </div>
  );
};

const Prose = ({ html }) => (
  <div style={{ fontSize: 14, color: B.textMd, lineHeight: 1.65 }} dangerouslySetInnerHTML={{ __html: html }} />
);

// A scorecard tile.
const Tile = ({ label, children, sub, first = false }) => (
  <div style={{
    padding: '13px 20px', borderLeft: first ? 'none' : `1px solid ${B.divider}`,
    display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0,
  }}>
    <span style={{ fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1, textTransform: 'uppercase', color: B.textLt }}>{label}</span>
    {children}
    {sub && <span style={{ fontSize: 11.5, color: B.textLt }}>{sub}</span>}
  </div>
);

const TileValue = ({ children, color = B.text }) => (
  <span style={{ fontSize: 21, fontWeight: weight.semibold, letterSpacing: -0.4, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

// Bottom line — the brief's own highlights, promoted from a bullet list to the
// page's lead block. Each claim carries the type weight; its support sits under
// it; the severity rail comes from the matching key finding where there is one.
const BottomLine = ({ statements, keyFindings }) => {
  if (!statements.length) return null;
  const graded = statements.map((s) => ({ ...s, sentiment: matchSentiment(`${s.lead} ${s.detail}`, keyFindings) }));
  const rank = (s) => (s.sentiment ? SENTIMENT_ORDER.indexOf(s.sentiment) : SENTIMENT_ORDER.length);
  const ordered = graded
    .map((s, i) => ({ s, i }))
    .sort((a, b) => (rank(a.s) - rank(b.s)) || (a.i - b.i))
    .map(({ s }) => s);

  return (
    <Panel>
      <CardHead title="Bottom line" hint={keyFindings.length ? 'most consequential first' : null} />
      <div style={{ padding: '2px 18px 14px' }}>
        {ordered.map((s, i) => {
          const meta = s.sentiment ? SENTIMENT[s.sentiment] : null;
          return (
            <div key={i} style={{ display: 'flex', gap: 13, padding: '13px 0', borderTop: i === 0 ? 'none' : `1px solid ${B.divider}` }}>
              <span aria-hidden="true" style={{
                width: 3, borderRadius: 2, flex: 'none', alignSelf: 'stretch',
                background: meta ? meta.color : B.border,
              }} />
              <div style={{ minWidth: 0 }}>
                <span style={{
                  display: 'block', fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 16.5,
                  fontWeight: weight.semibold, lineHeight: 1.35, color: B.text, marginBottom: 3,
                }}>{s.lead}</span>
                {s.detail && (
                  <div style={{ fontSize: 13.5, color: B.textMd, lineHeight: 1.55 }}
                    dangerouslySetInnerHTML={{ __html: s.detail }} />
                )}
                {meta && (
                  <span style={{ display: 'block', marginTop: 7, fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 0.7, textTransform: 'uppercase', color: meta.color }}>
                    {meta.legend}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

// Projected investor cash flow — opens with its own summary, and bar-scales the
// amounts so the shape of the deal is readable without parsing digits.
const CashFlow = ({ rows }) => {
  const { invested, returned, net, moic } = cashflowSummary(rows);
  const peak = rows.reduce((m, c) => Math.max(m, Math.abs(Number(c.amount) || 0)), 0) || 1;
  const tiles = [
    { label: 'Invested', value: fmt.currency(invested), color: B.text },
    { label: 'Returned', value: fmt.currency(returned), color: B.text },
    { label: 'Net', value: `${net >= 0 ? '+' : '−'}${fmt.currency(Math.abs(net))}`, color: net >= 0 ? B.green : B.red },
    ...(moic != null ? [{ label: 'MOIC', value: `${moic.toFixed(1)}×`, color: B.text }] : []),
  ];

  return (
    <Panel>
      <CardHead title="Projected investor cash flow" hint="extracted from the deal documents — verify against source" />
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, borderBottom: `1px solid ${B.divider}` }}>
        {tiles.map((t, i) => (
          <div key={t.label} style={{ padding: '11px 18px', borderLeft: i === 0 ? 'none' : `1px solid ${B.divider}` }}>
            <div style={{ fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1, textTransform: 'uppercase', color: B.textLt }}>{t.label}</div>
            <div style={{ fontSize: 17, fontWeight: weight.semibold, letterSpacing: -0.3, color: t.color, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr>
              {['When', 'Detail', 'Amount'].map((h, i) => (
                <th key={h} style={{
                  textAlign: i === 2 ? 'right' : 'left', fontSize: 10.5, fontWeight: weight.bold,
                  letterSpacing: 0.9, textTransform: 'uppercase', color: B.textLt,
                  padding: '9px 18px', background: B.surfaceMuted, borderBottom: `1px solid ${B.divider}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => {
              const out = c.direction === 'outflow';
              const amt = Math.abs(Number(c.amount) || 0);
              return (
                <tr key={i}>
                  <td style={{ padding: '10px 18px', borderBottom: `1px solid ${B.divider}`, color: B.textMd, whiteSpace: 'nowrap' }}>{c.period || '—'}</td>
                  <td style={{ padding: '10px 18px', borderBottom: `1px solid ${B.divider}`, color: B.textMd }}>{c.note || (out ? 'Investment' : 'Return')}</td>
                  <td style={{ padding: '10px 18px', borderBottom: `1px solid ${B.divider}`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                      <span aria-hidden="true" style={{
                        display: 'block', height: 8, borderRadius: 2, background: out ? B.red : B.green,
                        width: Math.max(4, Math.round((amt / peak) * 120)), opacity: 0.75,
                      }} />
                      <span style={{ fontWeight: weight.semibold, color: out ? B.red : B.green }}>
                        {out ? '−' : '+'}{fmt.currency(amt)}
                      </span>
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={2} style={{ padding: '10px 18px', borderTop: `2px solid ${B.border}`, background: B.surfaceMuted, fontWeight: weight.bold, color: B.text }}>
                Net to investor
              </td>
              <td style={{
                padding: '10px 18px', borderTop: `2px solid ${B.border}`, background: B.surfaceMuted,
                textAlign: 'right', whiteSpace: 'nowrap', fontWeight: weight.bold, color: net >= 0 ? B.green : B.red,
              }}>
                {net >= 0 ? '+' : '−'}{fmt.currency(Math.abs(net))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

// Key findings — the graded evidence, grouped by theme and filterable by
// severity, so "show me only the risks" is one click rather than a scroll.
const KeyFindings = ({ items }) => {
  const [filter, setFilter] = useState('all');
  const tally = tallyFindings(items);
  const shown = filter === 'all' ? items : items.filter((f) => (f?.sentiment || 'flag') === filter);
  const groups = groupKeyFindings(shown);

  const chips = [
    { key: 'all', label: 'All', n: tally.total, color: B.navy },
    ...SENTIMENT_ORDER
      .filter((s) => tally[s] > 0)
      .map((s) => ({ key: s, label: `${SENTIMENT[s].label}s`, n: tally[s], color: SENTIMENT[s].color })),
  ];

  return (
    <Panel>
      <CardHead title="Key findings" hint={`${tally.total} across ${groupKeyFindings(items).length} themes`} />
      <div style={{ display: 'flex', gap: 7, padding: '11px 18px', borderBottom: `1px solid ${B.divider}`, background: B.surfaceMuted, flexWrap: 'wrap' }}>
        {chips.map((c) => {
          const on = filter === c.key;
          return (
            <button key={c.key} type="button" onClick={() => setFilter(c.key)} aria-pressed={on}
              style={{
                border: `1px solid ${on ? c.color : B.border}`, background: on ? c.color : B.surface,
                color: on ? B.white : B.textMd, borderRadius: radius.pill, padding: '4px 11px',
                fontSize: 11.5, fontWeight: weight.semibold, fontFamily: 'inherit', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6, lineHeight: 1.4,
              }}>
              {c.key !== 'all' && !on && <Dot color={c.color} />}
              {c.label}
              <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{c.n}</span>
            </button>
          );
        })}
      </div>

      {groups.length === 0 ? (
        <div style={{ padding: '26px 18px', textAlign: 'center', color: B.textLt, fontSize: 13 }}>
          No {filter === 'all' ? '' : `${SENTIMENT[filter]?.label.toLowerCase()} `}findings in this run.
        </div>
      ) : groups.map((g, gi) => (
        <div key={gi} style={{ borderTop: gi === 0 ? 'none' : `1px solid ${B.divider}` }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9, padding: '9px 18px', background: B.surfaceSunken,
            fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1.1, textTransform: 'uppercase', color: B.textMd,
          }}>
            {g.name}
            <span style={{ marginLeft: 'auto', fontSize: 11, letterSpacing: 0, textTransform: 'none', color: B.textLt, fontVariantNumeric: 'tabular-nums' }}>
              {g.topics.reduce((n, t) => n + t.finds.length, 0)}
            </span>
          </div>
          {g.topics.map((t, ti) => (
            <div key={ti} style={{ padding: '12px 18px', borderTop: ti === 0 ? 'none' : `1px solid ${B.divider}` }}>
              {t.name && <div style={{ fontSize: 12, fontWeight: weight.bold, color: B.text, marginBottom: 6 }}>{t.name}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {t.finds.map((f, fi) => {
                  const meta = SENTIMENT[f.sentiment] || SENTIMENT.flag;
                  return (
                    <div key={fi} style={{ display: 'flex', gap: 11 }}>
                      <span aria-hidden="true" style={{ width: 3, borderRadius: 2, flex: 'none', alignSelf: 'stretch', background: meta.color }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: B.textMd, lineHeight: 1.55 }}>
                          {f.lead && <strong style={{ color: B.text, fontWeight: weight.semibold }}>{f.lead}. </strong>}
                          {f.detail}
                        </div>
                        <span style={{ display: 'inline-block', marginTop: 5 }}>
                          <Pill color={meta.color}>{meta.label}</Pill>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </Panel>
  );
};

// What the agents actually read — behind category tabs rather than four stacked
// lists, each item carrying its source as a host chip and its confidence as a meter.
const AgentFindings = ({ grouped }) => {
  const [tab, setTab] = useState(grouped[0]?.cat || null);
  const active = grouped.find((g) => g.cat === tab) || grouped[0];
  if (!active) return null;
  const sourced = active.items.filter((f) => f.sourceUrl).length;

  return (
    <Panel>
      <CardHead title="What the agents read" hint={`${active.items.length} findings · ${sourced} with a source`} />
      <div style={{ display: 'flex', gap: 2, padding: '0 10px', borderBottom: `1px solid ${B.divider}`, background: B.surfaceMuted, overflowX: 'auto' }}>
        {grouped.map(({ cat, items }) => {
          const meta = CATEGORY_META[cat] || { label: cat, color: B.navy };
          const on = active.cat === cat;
          return (
            <button key={cat} type="button" onClick={() => setTab(cat)} aria-pressed={on}
              style={{
                border: 'none', background: 'none', fontFamily: 'inherit', cursor: 'pointer',
                padding: '11px 12px 9px', fontSize: 12.5, fontWeight: weight.semibold, whiteSpace: 'nowrap',
                color: on ? meta.color : B.textMd, borderBottom: `2px solid ${on ? meta.color : 'transparent'}`,
                display: 'inline-flex', alignItems: 'center', gap: 7,
              }}>
              {meta.label}
              <span style={{ fontSize: 11, color: B.textLt, fontVariantNumeric: 'tabular-nums' }}>{items.length}</span>
            </button>
          );
        })}
      </div>
      {active.items.map((f, i) => (
        <div key={i} style={{ padding: '13px 18px', borderTop: i === 0 ? 'none' : `1px solid ${B.divider}` }}>
          <div style={{ fontSize: 13.5, fontWeight: weight.semibold, color: B.text, lineHeight: 1.4 }}>{f.title}</div>
          {f.summary && <div style={{ fontSize: 13, color: B.textMd, marginTop: 4, lineHeight: 1.55 }}>{f.summary}</div>}
          {(f.sourceUrl || f.confidence != null) && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <SourceChip url={f.sourceUrl} />
              <ConfidenceMeter value={f.confidence} />
            </div>
          )}
        </div>
      ))}
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

export const DealResearchTab = ({ dealId }) => {
  const { data, loading, error, reload } = useApi(
    () => api.get(`deals/${dealId}/research`),
    [dealId]
  );
  const [running, setRunning] = useState(false);
  const [runErr, setRunErr] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1100);
  const pollCount = useRef(0);
  const pollTimer = useRef(null);
  const sectionEls = useRef({});

  const status = data?.latest?.status || null;
  const isPending = status === 'pending';

  // The contents rail only earns its width on a wide viewport; below that the
  // report is a single column and the rail's cards fall in underneath.
  useEffect(() => {
    const check = () => setWide(window.innerWidth >= 1100);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Poll while the latest run is pending (bounded).
  useEffect(() => {
    if (!isPending) { pollCount.current = 0; return; }
    if (pollCount.current >= POLL_CAP) return;
    pollTimer.current = setTimeout(() => {
      pollCount.current += 1;
      reload();
    }, POLL_MS);
    return () => clearTimeout(pollTimer.current);
  }, [isPending, data, reload]);

  // Track which section the reader is in so the rail reflects position. Re-runs
  // when the data changes, since that is when the set of sections changes.
  useEffect(() => {
    const els = Object.entries(sectionEls.current).filter(([, el]) => el);
    if (!els.length || typeof IntersectionObserver === 'undefined') return;
    const seen = new Map();
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) seen.set(e.target.dataset.section, e.intersectionRatio);
      let best = null;
      let bestRatio = 0;
      for (const [id, ratio] of seen) if (ratio > bestRatio) { bestRatio = ratio; best = id; }
      if (best) setActiveSection(best);
    }, { rootMargin: '-80px 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] });
    els.forEach(([, el]) => obs.observe(el));
    return () => obs.disconnect();
  }, [data]);

  // `unlimited` powers the "Finish without the limit" action after a run
  // auto-stops at the token budget — it re-runs with a raised token ceiling.
  //
  // The run executes server-side WITHIN this request (reliable — it no longer
  // depends on the flaky queue worker) and takes a few minutes. So don't block
  // the UI on the long response: fire it, then poll for live progress and the
  // result. A gateway timeout on the long response is harmless — the run keeps
  // going server-side and polling reflects the true state.
  const runResearch = async ({ unlimited = false } = {}) => {
    setRunning(true);
    setRunErr(null);
    setShowHistory(false);
    pollCount.current = 0;
    api.post(`deals/${dealId}/research/run`, unlimited ? { unlimited: true } : {})
      .catch(() => { /* long-response/gateway timeout is expected — poll for state */ })
      .finally(() => reload());
    // Reflect the freshly-inserted 'pending' row, then the pending-poll takes over.
    await new Promise((r) => setTimeout(r, 1500));
    await reload();
    setRunning(false);
  };

  // Only take over the whole tab on the FIRST load (no data yet). Background
  // polls (reload every ~5s while a run is pending) also flip `loading`/`error`,
  // but blanking to a spinner each time makes the tab flicker every few seconds —
  // instead keep the current content on screen and let the data update silently.
  if (loading && !data) return <Spinner label="Loading research…" />;
  if (error && !data) return <div style={{ color: B.red, fontSize: 13, padding: '16px 0' }}>{error}</div>;

  const available    = data?.available !== false;
  const aiConfigured = data?.aiConfigured !== false;
  const findings     = data?.findings || [];
  const runs         = data?.runs || [];
  const latest       = data?.latest || null;

  // Defensive: never render raw JSON as the brief. If an older run stored the
  // brief as a JSON blob, extract the readable part (or hide it) client-side.
  const brief = (() => {
    const b = data?.brief;
    if (!b || typeof b !== 'string') return null;
    const t = b.trim();
    // Only treat as JSON when it's actually a JSON object that parses — otherwise
    // return the brief as-is (our [[SECTION]] format starts with '[', not JSON).
    if (t.startsWith('{')) {
      try { const o = JSON.parse(t); if (o && typeof o === 'object') return o.brief || o.summary || o.html || null; }
      catch { /* not JSON — fall through */ }
    }
    return b;
  })();

  const cashflow    = Array.isArray(data?.cashflow) ? data.cashflow : [];
  const keyFindings = Array.isArray(data?.keyFindings) ? data.keyFindings : [];
  // Cheap; no hook (avoids rules-of-hooks after the early returns above).
  const briefSections = splitBriefSections(brief);

  if (!available) {
    return (
      <div style={{ marginTop: 8, color: B.textLt, fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
        Deal research isn’t available yet — the database schema hasn’t been migrated.
      </div>
    );
  }

  const highlights = briefSections.find((s) => s.heading && /highlight/i.test(s.heading));
  // The lede is the short orienting section — it stays open. Excluded from the
  // highlights match so an untitled highlights block can't render twice.
  const lede = briefSections.find((s) => s !== highlights && (!s.heading || /what this is/i.test(s.heading)));
  const rest = briefSections.filter((s) => s !== highlights && s !== lede && s.heading);
  const statements = parseStatements(highlights?.body);

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, items: findings.filter((f) => f.category === cat) }))
    .filter((g) => g.items.length > 0);
  const sourceCount = findings.filter((f) => f.sourceUrl).length;
  const tally = tallyFindings(keyFindings);
  const cf = cashflowSummary(cashflow);

  const hasReport = !isPending && (statements.length > 0 || cashflow.length > 0 || keyFindings.length > 0 || findings.length > 0 || !!lede || rest.length > 0);

  // ---- Scorecard: only tiles whose figures are actually derivable ----------
  //
  // Three at most, each saying something the others don't. The full
  // invested/returned breakdown belongs to the cash-flow card — repeating it
  // here would spend the masthead's width on the same number twice.
  const tiles = [];
  if (cashflow.length) {
    tiles.push({
      id: 'net',
      label: 'Net to investor',
      node: <TileValue color={cf.net >= 0 ? B.green : B.red}>{`${cf.net >= 0 ? '+' : '−'}${fmt.currency(Math.abs(cf.net))}`}</TileValue>,
      sub: cf.moic != null ? `${cf.moic.toFixed(1)}× MOIC on ${fmt.currency(cf.invested)}` : 'no outflow modelled',
    });
  }
  if (tally.total) {
    tiles.push({
      id: 'tally',
      label: 'Findings',
      node: (
        <div>
          <div aria-hidden="true" style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: B.surfaceSunken, marginTop: 6, marginBottom: 5 }}>
            {SENTIMENT_ORDER.filter((s) => tally[s] > 0).map((s) => (
              <span key={s} style={{ display: 'block', height: '100%', width: `${(tally[s] / tally.total) * 100}%`, background: SENTIMENT[s].color }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, fontSize: 11, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
            {SENTIMENT_ORDER.filter((s) => tally[s] > 0).map((s) => (
              <span key={s} style={{ color: SENTIMENT[s].color }}>
                <strong style={{ fontWeight: weight.semibold }}>{tally[s]}</strong> {SENTIMENT[s].label.toLowerCase()}
              </span>
            ))}
          </div>
        </div>
      ),
    });
  }
  if (findings.length) {
    tiles.push({ id: 'sources', label: 'Sources', node: <TileValue>{sourceCount}</TileValue>, sub: `${grouped.length} of 4 agents reported` });
  }

  // ---- Contents rail entries ----------------------------------------------
  const nav = [];
  if (statements.length) nav.push({ id: 'bottom-line', label: 'Bottom line', count: statements.length });
  if (cashflow.length) nav.push({ id: 'cash-flow', label: 'Cash flow', count: cashflow.length });
  if (keyFindings.length) nav.push({ id: 'key-findings', label: 'Key findings', count: keyFindings.length });
  if (lede || rest.length) nav.push({ id: 'full-brief', label: 'Full brief', count: rest.length + (lede ? 1 : 0) });
  if (findings.length) nav.push({ id: 'agent-findings', label: 'What the agents read', count: findings.length });

  const setSection = (id) => (el) => { sectionEls.current[id] = el; };
  const goTo = (id) => {
    const el = sectionEls.current[id];
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- Masthead -----------------------------------------------------------
  const statusPill = isPending
    ? <Pill color={B.amber}><span aria-hidden="true" style={{
        width: 10, height: 10, borderRadius: '50%', border: `2px solid ${B.amber}55`, borderTopColor: B.amber,
        display: 'inline-block', animation: 'harvus-spin 0.7s linear infinite',
      }} />Running</Pill>
    : latest?.status === 'error' ? <Pill color={B.red}><Dot color={B.red} />Failed</Pill>
    : latest?.status === 'stopped' ? <Pill color={B.amber}><Dot color={B.amber} />Stopped early</Pill>
    : latest ? <Pill color={B.green}><Dot color={B.green} />Ready</Pill>
    : <Pill color={B.textLt}>Not run</Pill>;

  const masthead = (
    <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xxl, boxShadow: B.shadowXs, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap', padding: '16px 20px 14px' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{
            margin: '0 0 6px', fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 23,
            fontWeight: weight.semibold, letterSpacing: -0.3, color: B.text,
          }}>Research Brief</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: B.textMd, flexWrap: 'wrap' }}>
            {statusPill}
            {latest ? (
              <>
                <span>{isPending ? 'Started' : 'Run'} {fmt.dateTime(latest.completedUTC || latest.createdUTC)}</span>
                {latest.triggeredBy && <><Sep /><span style={{ textTransform: 'capitalize' }}>{latest.triggeredBy}</span></>}
                {grouped.length > 0 && <><Sep /><span>{grouped.length} agent{grouped.length === 1 ? '' : 's'} reported</span></>}
                {sourceCount > 0 && <><Sep /><span>{sourceCount} source{sourceCount === 1 ? '' : 's'}</span></>}
              </>
            ) : (
              <span style={{ color: B.textLt }}>No research has been run on this deal.</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', position: 'relative' }}>
          {runs.length > 1 && (
            <Btn size="sm" variant="secondary" onClick={() => setShowHistory((s) => !s)}>
              History <span style={{ opacity: 0.7 }}>{runs.length}</span>
            </Btn>
          )}
          <Btn size="sm" onClick={() => runResearch()} disabled={running || isPending} loading={running || isPending}>
            {latest ? 'Re-run research' : 'Run research'}
          </Btn>
          {showHistory && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 20, minWidth: 320,
              background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xl,
              boxShadow: B.shadowMd, padding: '6px 0', maxHeight: 300, overflowY: 'auto',
            }}>
              <div style={{ padding: '6px 14px 8px', fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1, textTransform: 'uppercase', color: B.textLt }}>
                Run history
              </div>
              {runs.slice(0, 10).map((r) => (
                <div key={r.runId} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12, padding: '5px 14px', color: B.textMd }}>
                  <Dot color={r.status === 'ready' ? B.green : r.status === 'error' ? B.red : B.amber} />
                  <span style={{ width: 118, flex: 'none', fontVariantNumeric: 'tabular-nums' }}>{fmt.dateTime(r.completedUTC || r.createdUTC)}</span>
                  <span style={{ width: 62, flex: 'none', color: B.textLt, textTransform: 'capitalize' }}>{r.triggeredBy}</span>
                  <span style={{ color: B.textLt }}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {tiles.length > 0 && !isPending && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))`, borderTop: `1px solid ${B.divider}` }}>
          {tiles.map((t, i) => (
            <Tile key={t.id} label={t.label} sub={t.sub} first={i === 0}>{t.node}</Tile>
          ))}
        </div>
      )}
    </div>
  );

  // ---- States -------------------------------------------------------------
  const runningPanel = isPending && (
    <Panel>
      <div style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: weight.semibold, color: B.text }}>
          <span aria-hidden="true" style={{
            width: 12, height: 12, borderRadius: '50%', border: `2px solid ${B.accent}44`, borderTopColor: B.accent,
            display: 'inline-block', animation: 'harvus-spin 0.7s linear infinite', flex: 'none',
          }} />
          {latest?.message || 'Researching this deal…'}
        </div>
        <div aria-hidden="true" style={{ height: 5, borderRadius: 3, background: B.surfaceSunken, overflow: 'hidden', margin: '12px 0 10px' }}>
          <div style={{ height: '100%', width: '38%', background: B.accent, borderRadius: 3, animation: 'harvus-research-sweep 1.6s ease-in-out infinite' }} />
        </div>
        <div style={{ fontSize: 12.5, color: B.textMd, lineHeight: 1.6 }}>
          Four agents read this deal’s documents, then search public filings, comparables and sponsor records.
          A full run takes a few minutes.
        </div>
        <div style={{ fontSize: 11.5, color: B.textLt, marginTop: 8 }}>
          Runs on the server — you can leave this page and come back.
          {latest?.createdUTC && ` Started ${fmt.dateTime(latest.createdUTC)}.`}
        </div>
      </div>
      <style>{'@keyframes harvus-research-sweep{0%{margin-left:-38%}100%{margin-left:100%}}'}</style>
    </Panel>
  );

  const emptyPanel = !isPending && !latest && (
    <Panel>
      <div style={{ padding: '38px 24px', textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
        <div aria-hidden="true" style={{ fontSize: 26, color: B.textLt, marginBottom: 8 }}>◎</div>
        <div style={{ fontSize: 15, fontWeight: weight.semibold, color: B.text, marginBottom: 6 }}>No research on this deal yet</div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
          Four agents will read this deal’s documents, then search public filings, comparables and sponsor
          records — and write up what they find as a brief with a projected investor cash flow.
        </p>
        <Btn size="sm" onClick={() => runResearch()} disabled={running || !aiConfigured}>Run research</Btn>
      </div>
    </Panel>
  );

  const stoppedPanel = latest?.status === 'stopped' && !isPending && (
    <div style={{
      background: `${B.amber}12`, border: `1px solid ${B.amber}4d`, borderRadius: radius.xxl,
      padding: '14px 18px', display: 'flex', gap: 13,
    }}>
      <span aria-hidden="true" style={{ width: 3, borderRadius: 2, flex: 'none', alignSelf: 'stretch', background: B.amber }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: weight.semibold, color: B.text, marginBottom: 3 }}>Stopped early to control cost</div>
        <div style={{ fontSize: 12.5, color: B.textMd, lineHeight: 1.55 }}>
          {latest.message || 'This run hit its token budget before finishing. Everything below is complete — but some sections may be missing.'}
        </div>
        <div style={{ marginTop: 11 }}>
          <Btn size="sm" onClick={() => runResearch({ unlimited: true })} disabled={running || isPending} loading={running || isPending}>
            Finish without the limit
          </Btn>
        </div>
      </div>
    </div>
  );

  const errorPanel = latest?.status === 'error' && !isPending && (
    <div style={{
      background: `${B.red}10`, border: `1px solid ${B.red}45`, borderRadius: radius.xxl,
      padding: '14px 18px', display: 'flex', gap: 13,
    }}>
      <span aria-hidden="true" style={{ width: 3, borderRadius: 2, flex: 'none', alignSelf: 'stretch', background: B.red }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: weight.semibold, color: B.text, marginBottom: 3 }}>This run didn’t finish</div>
        <div style={{ fontSize: 12.5, color: B.textMd, lineHeight: 1.55 }}>
          {latest.message || 'The run failed before it could write a brief.'} Re-running usually clears a one-off failure.
        </div>
      </div>
    </div>
  );

  // ---- Rail ---------------------------------------------------------------
  const rail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      {nav.length > 1 && (
        <div style={{
          background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xxl,
          boxShadow: B.shadowXs, overflow: 'hidden', position: wide ? 'sticky' : 'static', top: 14,
        }}>
          <div style={{ padding: '12px 15px 8px', fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1.1, textTransform: 'uppercase', color: B.textLt }}>
            On this page
          </div>
          <nav style={{ display: 'flex', flexDirection: 'column', padding: '0 8px 10px' }}>
            {nav.map((n) => {
              const on = activeSection === n.id;
              return (
                <button key={n.id} type="button" onClick={() => goTo(n.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: radius.lg,
                    border: 'none', background: on ? B.accentLt : 'transparent', fontFamily: 'inherit',
                    fontSize: 13, textAlign: 'left', cursor: 'pointer',
                    color: on ? B.accent : B.textMd, fontWeight: on ? weight.semibold : weight.regular,
                  }}>
                  {n.label}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: B.textLt, fontVariantNumeric: 'tabular-nums' }}>{n.count}</span>
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xxl, boxShadow: B.shadowXs, overflow: 'hidden' }}>
        <div style={{ padding: '12px 15px 8px', fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1.1, textTransform: 'uppercase', color: B.textLt }}>
          Agent coverage
        </div>
        {CATEGORY_ORDER.map((cat, i) => {
          const meta = CATEGORY_META[cat];
          const items = findings.filter((f) => f.category === cat);
          const ran = items.length > 0;
          const srcs = items.filter((f) => f.sourceUrl).length;
          return (
            <div key={cat} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '8px 15px', borderTop: i === 0 ? 'none' : `1px solid ${B.divider}`, fontSize: 12.5 }}>
              <span aria-hidden="true" style={{ color: ran ? B.green : B.textLt, fontSize: 12, lineHeight: 1.5, flex: 'none' }}>{ran ? '✓' : '–'}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: ran ? B.text : B.textLt, fontWeight: weight.medium, lineHeight: 1.35 }}>{meta.label}</div>
                <div style={{ fontSize: 11, color: B.textLt, fontVariantNumeric: 'tabular-nums' }}>
                  {ran ? `${srcs} source${srcs === 1 ? '' : 's'} · ${items.length} finding${items.length === 1 ? '' : 's'}` : 'nothing reported'}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ padding: '12px 15px 15px', fontSize: 11.5, color: B.textLt, lineHeight: 1.5, borderTop: `1px solid ${B.divider}` }}>
          Gathered by AI from public sources and this deal’s own documents —{' '}
          <strong style={{ color: B.textMd, fontWeight: weight.semibold }}>verify before relying on it.</strong>
        </div>
      </div>
    </div>
  );

  // ---- Report -------------------------------------------------------------
  const report = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      {statements.length > 0 && (
        <div ref={setSection('bottom-line')} data-section="bottom-line">
          <BottomLine statements={statements} keyFindings={keyFindings} />
        </div>
      )}

      {cashflow.length > 0 && (
        <div ref={setSection('cash-flow')} data-section="cash-flow">
          <CashFlow rows={cashflow} />
        </div>
      )}

      {keyFindings.length > 0 && (
        <div ref={setSection('key-findings')} data-section="key-findings">
          <KeyFindings items={keyFindings} />
        </div>
      )}

      {(lede || rest.length > 0) && (
        <div ref={setSection('full-brief')} data-section="full-brief">
          <Panel>
            <CardHead title="Full brief" hint="the write-up behind the summary" />
            {lede && (
              <div style={{ padding: '14px 18px 4px' }}>
                {lede.heading && (
                  <div style={{ fontSize: 10.5, fontWeight: weight.bold, letterSpacing: 1, textTransform: 'uppercase', color: B.textLt, marginBottom: 7 }}>
                    {lede.heading}
                  </div>
                )}
                <Prose html={lede.body} />
              </div>
            )}
            {rest.map((s, i) => (
              <Collapsible key={i} title={s.heading} preview={sectionPreview(s.body)}>
                <Prose html={s.body} />
              </Collapsible>
            ))}
          </Panel>
        </div>
      )}

      {grouped.length > 0 && (
        <div ref={setSection('agent-findings')} data-section="agent-findings">
          <AgentFindings grouped={grouped} />
        </div>
      )}

      {!isPending && latest && !hasReport && (
        <Panel>
          <div style={{ padding: '32px 20px', textAlign: 'center', color: B.textMd, fontSize: 13 }}>
            This run finished without returning anything usable. Re-running often helps.
          </div>
        </Panel>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {masthead}

      {!aiConfigured && (
        <div style={{ fontSize: 12.5, color: B.amber }}>
          No AI connection is configured — research can’t run until one is set up on Settings → AI Connections.
        </div>
      )}
      {runErr && <div style={{ fontSize: 12.5, color: B.red }}>{runErr}</div>}

      {stoppedPanel}
      {errorPanel}
      {runningPanel}
      {emptyPanel}

      {hasReport && (
        wide ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 244px', gap: 16, alignItems: 'start' }}>
            {report}
            {rail}
          </div>
        ) : (
          <>
            {report}
            {rail}
          </>
        )
      )}
    </div>
  );
};
